/**
 * @file share model 接入分享中台
 *
 */
import {
    BaseModel,
    MutationResult,
    restMutation,
} from 'vue-apollo-model';


export type baseConfigParams = {
    subBiz: string;
    sdkVersion: string;
};

export interface ExtTokenStoreParams {
    title?: string;
    subTitle?: string;
    coverUrl?: string;
    nickname?: string;
    videoType?: 'short-video' | 'video';
    photoId?: string;
    queries?: string;
    isDataReport?: boolean;
    canShowDialog?: boolean;
}

interface ShareParams {
    kpn: string;
    subBiz: string;
    shareObjectId: string;
    shareChannel: string;
    shareMethod?: 'CARD' | 'TOKEN' | 'PICTURE';
    sdkVersion: string;
    kpf: string;
    extTokenStoreParams?: ExtTokenStoreParams;
}

interface ShareLogParams {
    kpn: string;
    subBiz: string;
    token: string;
    did: string;
    isSmallApp: boolean;
    url: string;
}

export class ShareModel extends BaseModel {
    // 获取分享中台的分享信息接口 https://wiki.corp.kuaishou.com/pages/viewpage.action?pageId=247606024
    @restMutation<ShareModel>({
        url: '/rest/zt/share/w/any',
        method: 'POST',
        headers: {
            contentType: 'application/x-www-form-urlencoded',
        },
        variables(shareParams) {
            return shareParams;
        },
    })
    private shareMutation!: MutationResult<ShareParams, {
        result: number;
        error_msg?: string;
        share: {
            shareObject: {
                shareId: string;
                shareObjectId: string;
                shareResourceType: string;
                coverUrls?: string[];
                subTitle?: string;
                shareUrl?: string;
                title?: string;
                qrBytes?: string[];
                shareMessage?: string;
                extParams: {
                    sharePath: string;
                };
            };
        };
    }>;

    // 分享打点上报，https://wiki.corp.kuaishou.com/pages/viewpage.action?pageId=369682667
    @restMutation<ShareModel>({
        url: '/rest/zt/share/w/tp/once',
        method: 'POST',
        headers: {
            contentType: 'application/x-www-form-urlencoded',
        },
        variables(shareParams) {
            return shareParams;
        },
    })
    private shareLogMutation!: MutationResult<ShareLogParams, {
        result: number;
        error_msg?: string;
    }>;

    async handleShareRequest<T extends Record<string, string>>(params: {
        shareChannel: string;
        shareMethod?: 'CARD' | 'TOKEN' | 'PICTURE';
        shareObjectId: string;
        subBiz?: string;
        sdkVersion?: string;
        extTokenStoreParams?: ExtTokenStoreParams;
        extTransientParams?: T;
        extRecoParams?: T;
    }, config: baseConfigParams) {
        const shareParams = Object.assign(
            {
                kpn: 'KUAISHOU_VISION',
                kpf: 'PC_WEB',
            },
            config,
            params,
        );
        await this.shareMutation.mutate(shareParams);
    }

    async handleQrShare<T extends ExtTokenStoreParams>(shareId: string, {
        config,
        shareExtInfo,
    }: {
        config: baseConfigParams,
        shareExtInfo?: T,
    }) {
        await this.handleShareRequest({
            shareChannel: 'WECHAT',
            shareMethod: 'PICTURE',
            shareObjectId: shareId,
            extTokenStoreParams: shareExtInfo,
        }, config);
    }

    async handleTokenShare<T extends Record<string, any>, U extends ExtTokenStoreParams>(shareId: string,
        {
            config,
            shareExtInfo,
            extTransientParams,
            extRecoParams,
        }: {
            config: baseConfigParams,
            shareExtInfo?: U,
            extTransientParams?: T,
            extRecoParams?: T
        }) {
        await this.handleShareRequest({
            shareChannel: 'COPY_LINK',
            shareMethod: 'TOKEN',
            shareObjectId: shareId,
            extTokenStoreParams: shareExtInfo,
            extTransientParams,
            extRecoParams,
        }, config);
    }

    async handleCardShare({
        shareId,
        shareChannel,
        shareInfo,
        config,
    }: {
        shareId: string,
        shareChannel: string,
        shareInfo: ExtTokenStoreParams,
        config: baseConfigParams
    },
    ) {
        await this.handleShareRequest({
            shareChannel,
            shareMethod: 'CARD',
            shareObjectId: shareId,
            extTokenStoreParams: shareInfo,
        }, config);
    }

    async sendShareLog<T extends Record<string, string>>(params: T, config: baseConfigParams) {
        const shareLogParams = Object.assign(
            {
                kpn: 'KUAISHOU_VISION',
                isSmallApp: false,
            },
            config,
            params,
        );
        await this.shareLogMutation.mutate(shareLogParams as unknown as ShareLogParams);
    }

    get shareResult() {
        return this.shareMutation.data.result;
    }

    get cardShareObj() {
        const { shareId, shareObjectId, shareResourceType, coverUrls = [], subTitle, shareUrl, title } = this.shareMutation.data.share.shareObject;
        return {
            shareId,
            shareObjectId,
            shareResourceType,
            image: coverUrls[0] || '',
            summary: subTitle,
            url: shareUrl,
            title,
        };
    }

    get tokenShareObj() {
        if (this.shareMutation.data.share) {
            const { shareId, shareObjectId, shareResourceType, shareMessage } = this.shareMutation.data.share.shareObject;
            return {
                shareId,
                shareObjectId,
                shareResourceType,
                tokenUrl: shareMessage,
            };
        }
        return {};
    }

    get qrShareObj() {
        if (this.shareMutation.data.share) {
            const { shareId, shareObjectId, shareResourceType, shareUrl } = this.shareMutation.data.share.shareObject;
            return {
                shareId,
                shareObjectId,
                shareResourceType,
                qrUrl: shareUrl,
            };
        }
        return {};
    }
}


